import { reactive } from 'vue';
import makeStoreDebugAccessible from '@/core/store/store-debug';
import {
    TempBasketApiClientService,
    TempBasketViewModel,
    TmpAddOrUpdateModuleConfigurationRequest,
} from './temp-basket-api';

import { filter, isArray, map, sum } from 'lodash';
import Deferred from '@/core/async/Deferred';

class BasketStore {
    private state: IBasketStoreState = reactive({
        basket: {} as TempBasketViewModel,
        selectedPaymentMethod: '',
    });

    private deferred = new Deferred<IBasketStoreState>();
    public initialized: Promise<IBasketStoreState>;

    public constructor() {
        makeStoreDebugAccessible('basketStore', this.state);
        this.initialized = this.deferred.promise;
    }

    public async get(): Promise<TempBasketViewModel> {
        return this.state.basket = await TempBasketApiClientService.get();
    }

    public async init(): Promise<TempBasketViewModel> {
        try {
            return this.get().then(() => this.deferred.resolve(this.state.basket));
        } catch (e) {
            return Promise.reject();
        }
    }

    public async add(sku: string | string[], quantity: number) {
        const isMultipleAdd = isArray(sku);
        return this.state.basket = isMultipleAdd
            ? await this.addMultiple(sku as string[], quantity)
            : await this.addSingle(sku as string, quantity);
    }

    public get basket(): TempBasketViewModel {
        return this.state.basket;
    }

    public get quantity(): number {
        const filteredLineItems = filter(this.state.basket.lineItems, lineItem => {
            return !(lineItem.type === 'Regular' && lineItem.moduleSofaReference !== undefined);
        });
        const quantityArray = map(filteredLineItems, lineItem => lineItem.quantity);

        return sum(quantityArray);
    }

    public get lineItems(): any[] {
        return this.state.basket?.lineItems ?? [];
    }

    public get pickupStoreId(): string | undefined {
        return this.state.basket.pickupStoreId || undefined;
    }

    public get selectedPaymentMethod() {
        return this.state.selectedPaymentMethod;
    }

    public async replaceItem(sku: string, lineItemId: string) {
        return this.state.basket = await TempBasketApiClientService.replaceItem({
            replacePairs: [{
                sourceId: sku,
                sourceLineItemId: lineItemId,
                targetId: sku,
            }],
        });
    }

    public async addPickAndPack(sku: string, quantity: number) {
        return this.state.basket = await TempBasketApiClientService.addPickAndPack({
            sku,
            quantity,
        });
    }

    public async addB2bExpress(sku: string, quantity: number, delta: boolean = false, messageId?: string) {
        return this.state.basket = await TempBasketApiClientService.addB2bExpress({
            sku,
            quantity,
            delta,
        }, messageId);
    }

    public async addB2b(sku: string, quantity: number, delta: boolean = false) {
        const lineItemId = this.state.basket.lineItems?.find((item) => item.sku === sku);

        const request = {
            sku,
            quantity,
            delta,
        };

        if (lineItemId?.id) {
            return this.state.basket = await TempBasketApiClientService.addB2b(request, lineItemId.id);
        }

        return this.state.basket = await TempBasketApiClientService.add({
            sku,
            quantity,
        });
    }

    public async removeItem(sku: string, messageId?: string) {
        const lineItemId = this.state.basket.lineItems?.find((item) => item.sku === sku)?.id;

        if (!lineItemId) {
            return;
        }

        return this.state.basket = await TempBasketApiClientService.removeItem({}, lineItemId, messageId);
    }

    public async updateB2bExpress(sku:string, expressDelivery: boolean, messageId?: string) {
        const lineItemId = this.state.basket?.lineItems?.find((item) => item.sku === sku)?.id;

        if (!lineItemId) {
            return this.state.basket;
        }

        const request = {
            expressDelivery,
        };

        return this.state.basket = await TempBasketApiClientService.updateB2bExpress(request, lineItemId, messageId);
    }

    public async addClickAndCollect(sku: string, quantity: number, storeId: string) {
        return this.state.basket = await TempBasketApiClientService.addClickAndCollect({
            sku,
            quantity,
            storeId,
        });
    }

    private async addSingle(sku: string, quantity: number) {
        return await TempBasketApiClientService.add({
            sku,
            quantity,
        });
    }

    private async addMultiple(skus: string[], quantity: number) {
        const request = skus.map((sku) => {
            return { sku, quantity };
        });

        return await TempBasketApiClientService.addMultiple(request);
    }

    public async addMultipleWithQuantity(request: { sku: string, quantity: number }[]) {
        return this.state.basket = await TempBasketApiClientService.addMultiple(request);
    }

    async addOffer(offerId: string): Promise<TempBasketViewModel> {
        return this.state.basket = await TempBasketApiClientService.addOffer(offerId);
    }

    async addOfferItem(offerId: string, lineItemId: string): Promise<TempBasketViewModel> {
        return this.state.basket = await TempBasketApiClientService.addOfferItem(offerId, lineItemId);
    }

    public async addOutletItem(recId: number, messageId?: string) {
        return this.state.basket = await TempBasketApiClientService.addOutletItem({
            recId,
            quantity: 1,
        }, messageId);
    }

    public async outletItemInStock(recId: number) {
        return await TempBasketApiClientService.outletItemInStock({
            recId,
            quantity: 1,
        });
    }

    public setSelectedPaymentMethod(method: string) {
        this.state.selectedPaymentMethod = method;
    }

    public async addOrUpdateModuleConfiguration(configurationId: string, request: TmpAddOrUpdateModuleConfigurationRequest, messageId?: string) {
        return this.state.basket = await TempBasketApiClientService.addOrUpdateModuleConfiguration(configurationId, request, messageId);
    }
}

export interface IBasketStoreState {
    basket: TempBasketViewModel;
    selectedPaymentMethod: string;
}

export default new BasketStore();
